import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Spinner, Tab } from '../../components/design-system';
import {
  fill, summarize, troubleReports,
  type AdminQuestion, type AdminSurvey, type QuestionSummary,
} from '../../lib/tripSurvey';
import { AdminPage } from './AdminPage';
import { SurveyQuestionEditor } from './SurveyQuestions';

interface Overview {
  sent_count: number;
  questions: AdminQuestion[];
  surveys: AdminSurvey[];
}

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="bg-app-card border border-app-border rounded-xl px-4 py-3">
      <div className="text-xs text-app-muted">{label}</div>
      <div className="font-serif text-[26px] text-app-fg leading-tight">{value}</div>
      {hint && <div className="text-[11px] text-app-muted mt-0.5">{hint}</div>}
    </div>
  );
}

const pct = (n: number, total: number) => (total ? `${Math.round((n / total) * 100)}%` : '–');
const show = (n: number | null | undefined) => (n === null || n === undefined ? '–' : n.toFixed(1));

function SummaryCard({ s }: { s: QuestionSummary }) {
  const q = s.question;
  const total = s.counts?.reduce((sum, c) => sum + c.n, 0) ?? 0;
  return (
    <div className="bg-app-card border border-app-border rounded-xl px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm text-app-fg">{fill(q.label, {})}</div>
        <div className="text-xs text-app-muted whitespace-nowrap">
          {s.count} answer{s.count === 1 ? '' : 's'}{q.archived_at ? ' · archived' : ''}
        </div>
      </div>
      {s.average !== undefined && (
        <div className="text-sm text-app-fg-2 mt-1">
          Average {show(s.average)}{q.kind === 'stars' ? ' of 5' : ' of 10'}
          {s.nps !== undefined && s.nps !== null ? ` · recommend score ${s.nps}` : ''}
        </div>
      )}
      {s.counts && (
        <div className="mt-2 flex flex-col gap-1">
          {s.counts.map((c) => (
            <div key={c.label} className="flex items-center gap-2 text-xs">
              <span className="w-28 shrink-0 truncate text-app-fg-2">{c.label}</span>
              <div className="flex-1 h-1.5 rounded-full bg-app-border overflow-hidden">
                <div className="h-full rounded-full bg-violet" style={{ width: pct(c.n, total) }} />
              </div>
              <span className="w-14 text-right text-app-muted">{c.n} · {pct(c.n, total)}</span>
            </div>
          ))}
        </div>
      )}
      {s.texts && (
        <ul className="mt-2 flex flex-col gap-1">
          {s.texts.map((t, i) => <li key={i} className="text-sm text-app-fg-2">“{t}”</li>)}
        </ul>
      )}
    </div>
  );
}

function Results({ data }: { data: Overview }) {
  const { questions, surveys } = data;
  const summaries = useMemo(() => summarize(questions, surveys), [questions, surveys]);
  const trouble = useMemo(() => troubleReports(questions, surveys), [questions, surveys]);
  const byRole = (role: AdminQuestion['role']) => summaries.find((s) => s.question.role === role);
  const sent = Number(data.sent_count);

  if (surveys.length === 0) return <p className="text-sm text-app-muted py-10 text-center">No answers yet.</p>;

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Tile label="Answered" value={`${surveys.length}`} hint={`${pct(surveys.length, sent)} of ${sent} sent`} />
        <Tile label="Trip overall" value={show(byRole('overall')?.average)} hint="out of 5" />
        <Tile label="Host" value={show(byRole('host_rating')?.average)} hint="out of 5" />
        <Tile label="Recommend score" value={byRole('nps')?.nps == null ? '–' : `${byRole('nps')!.nps}`} hint="-100 to 100" />
      </div>

      {trouble.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-crimson mb-2">Needs attention ({trouble.length})</h2>
          <div className="flex flex-col gap-2">
            {trouble.map(({ survey, note }) => (
              <div key={survey.id} className="border border-crimson/30 bg-crimson/5 rounded-xl px-4 py-3">
                <div className="text-sm text-app-fg">
                  {survey.traveler_name ?? 'Traveler'} · host {survey.host_name ?? 'unknown'}{survey.trip_name ? ` · ${survey.trip_name}` : ''}
                </div>
                <p className="text-sm text-app-fg-2 mt-1">{note ?? 'No details given.'}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="text-sm font-semibold text-app-fg mb-2">By question</h2>
      <div className="flex flex-col gap-2 mb-8">{summaries.map((s) => <SummaryCard key={s.question.id} s={s} />)}</div>

      <h2 className="text-sm font-semibold text-app-fg mb-2">Each traveler</h2>
      <div className="flex flex-col gap-2">
        {surveys.map((sv) => (
          <details key={sv.id} className="bg-app-card border border-app-border rounded-xl px-4 py-3">
            <summary className="cursor-pointer flex items-center justify-between gap-3 text-sm text-app-fg">
              <span className="truncate">
                {sv.traveler_name ?? 'Traveler'} → {sv.host_name ?? 'host'}{sv.trip_name ? ` · ${sv.trip_name}` : ''}
              </span>
              <span className="text-xs text-app-muted whitespace-nowrap">{new Date(sv.completed_at).toLocaleDateString()}</span>
            </summary>
            <dl className="mt-3 flex flex-col gap-2">
              {sv.answers.map((a) => (
                <div key={a.question_id}>
                  <dt className="text-xs text-app-muted">{fill(a.label, { host: sv.host_name?.split(' ')[0], trip: sv.trip_name })}</dt>
                  <dd className="text-sm text-app-fg-2">
                    {renderValue(a.value, questions.find((q) => q.id === a.question_id))}
                    {a.note ? <span className="text-app-muted"> · “{a.note}”</span> : null}
                  </dd>
                </div>
              ))}
            </dl>
          </details>
        ))}
      </div>
    </>
  );
}

function renderValue(value: unknown, q: AdminQuestion | undefined): string {
  if (q?.kind === 'stars') return `${value} / 5`;
  if (q?.kind === 'scale10') return `${value} / 10`;
  const label = (k: string) => q?.options.find((o) => o.key === k)?.label ?? k;
  if (q?.kind === 'yesno') return label(value === true ? 'yes' : 'no');
  if (q?.kind === 'choice') return label(String(value));
  if (q?.kind === 'multi' && Array.isArray(value)) return value.map((k) => label(String(k))).join(', ');
  return String(value);
}

export function AdminSurveys() {
  const [tab, setTab] = useState<'results' | 'questions'>('results');
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data: overview, error: err } = await supabase.rpc('admin_trip_survey_overview');
    if (err) setError(err.message);
    else setData(overview as Overview);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <AdminPage
      title="Trip surveys"
      description="Sent to travelers 12 hours after the last day (push + email), with one last reminder by email only after 3 days. The link closes after 7 days."
    >
      <div className="flex gap-1.5 mb-6">
        <Tab active={tab === 'results'} onClick={() => setTab('results')}>Results</Tab>
        <Tab active={tab === 'questions'} onClick={() => setTab('questions')}>Questions</Tab>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : (
        <>
          {error && <p className="text-sm text-crimson mb-4">{error}</p>}
          {data && (tab === 'results'
            ? <Results data={data} />
            : <SurveyQuestionEditor questions={data.questions} onChanged={load} />)}
        </>
      )}
    </AdminPage>
  );
}

export default AdminSurveys;
